/* eslint-disable jsx-a11y/no-autofocus -- Preserve only caller-requested native form autofocus; it is off by default. */
import * as React from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Paper, { type PaperProps } from '@mui/material/Paper';
import { Check, Search, X } from 'lucide-react';
import { useDepartmentLocale } from '../../ui/locale/departmentLocale';
import { useOptionalLanguage } from '../../context/LanguageContext';
import { translateKey } from '../../ui/locale/centralLocalization';

const DropdownFooterContext = React.createContext<{
  content: React.ReactNode; id: string; input: () => HTMLInputElement | null;
  close: () => void; escape: () => void;
} | null>(null);

const DropdownPaper = React.forwardRef<HTMLDivElement, PaperProps>(function DropdownPaper({ children, style, ...props }, ref) {
  const footer = React.useContext(DropdownFooterContext);
  return <Paper {...props} ref={ref} style={{ ...style, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100dvh - 24px)' }}>
    {children}
    {footer && <div role="presentation" data-aa-dropdown-footer={footer.id} data-aa-searchable="true" style={{ flex: 'none', borderTop: '1px solid var(--crm-border, #d9d5cc)' }}
      onMouseDown={event => event.preventDefault()}
      onClickCapture={event => { if ((event.target as Element).closest('button:not(:disabled)')) footer.input()?.focus(); }}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null) && event.relatedTarget !== footer.input()) footer.close(); }}
      onKeyDown={event => {
        const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); footer.escape(); }
        if (event.key === 'Tab' && ((event.shiftKey && event.target === buttons[0]) || (!event.shiftKey && event.target === buttons.at(-1)))) {
          event.preventDefault(); if (!event.shiftKey) footer.close(); footer.input()?.focus();
        }
      }}>{footer.content}</div>}
  </Paper>;
});

export interface SearchableDropdownOption {
  value: string | number;
  label: string;
  hint?: string;
  searchText?: string;
  disabled?: boolean;
}

export interface SearchableDropdownProps {
  options: SearchableDropdownOption[];
  value?: string | number | readonly (string | number)[] | null;
  defaultValue?: string | number | readonly (string | number)[] | null;
  onChange?: (value: string | number | (string | number)[] | null) => void;
  placeholder?: string;
  multiple?: boolean;
  limitTags?: number;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  loading?: boolean;
  clearable?: boolean;
  error?: boolean;
  direction?: 'rtl' | 'ltr';
  id?: string;
  name?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  autoFocus?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
  noResultsText?: string;
  loadingText?: string;
  clearLabel?: string;
  requiredMessage?: string;
  onSearchChange?: (query: string) => void;
  searchValue?: string;
  serverFiltered?: boolean;
  /** Keep a server-search criterion while using pagination outside the menu. */
  preserveSearchOnClose?: boolean;
  dropdownFooter?: React.ReactNode;
  freeText?: boolean;
  tokenSeparators?: readonly string[];
}

/** Shared search and selection control. Values remain owned by the caller. */
export function SearchableDropdown({
  options, value: controlledValue, defaultValue = null, onChange, placeholder, multiple = false, limitTags,
  disabled = false, readOnly = false, required = false, loading = false,
  clearable = true, error = false, direction, id, name, ariaLabel, ariaLabelledBy, ariaDescribedBy,
  autoFocus, onBlur, onFocus, inputRef: externalInputRef,
  noResultsText, loadingText,
  clearLabel, requiredMessage, onSearchChange, searchValue, serverFiltered = false, preserveSearchOnClose = false, dropdownFooter, freeText = false, tokenSeparators,
}: SearchableDropdownProps) {
  const locale = useDepartmentLocale();
  const appLocale = useOptionalLanguage();
  const language = locale?.language ?? appLocale?.language;
  const text = (value: string) => locale?.t(value) ?? appLocale?.t(value) ?? value;
  const controlText = (key: string) => translateKey(key, language ?? 'en');
  const displayPlaceholder = placeholder ?? controlText('common.select');
  const validationMessage = requiredMessage ?? controlText('common.requiredSelection');
  const [localQuery, setQuery] = React.useState('');
  const query = searchValue ?? localQuery;
  const [open, setOpen] = React.useState(false);
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const [fieldsetDisabled, setFieldsetDisabled] = React.useState(false);
  const effectiveDisabled = disabled || fieldsetDisabled;
  const value = controlledValue === undefined ? uncontrolledValue : controlledValue;
  const changeValue = (next: string | number | (string | number)[] | null) => {
    if (effectiveDisabled || readOnly || inputRef.current?.matches(':disabled')) return;
    if (controlledValue === undefined) setUncontrolledValue(next);
    onChange?.(next);
  };
  const inputRef = React.useRef<HTMLInputElement>(null);
  const footerId = React.useId();
  const assignInputRef = React.useCallback((input: HTMLInputElement | null) => {
    inputRef.current = input;
    if (typeof externalInputRef === 'function') externalInputRef(input);
    else if (externalInputRef) externalInputRef.current = input;
  }, [externalInputRef]);
  const highlightedOption = React.useRef<SearchableDropdownOption | null>(null);
  React.useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const fieldsets: HTMLFieldSetElement[] = [];
    for (let parent = input.parentElement; parent; parent = parent.parentElement) {
      if (parent instanceof HTMLFieldSetElement) fieldsets.push(parent);
    }
    const sync = () => setFieldsetDisabled(fieldsets.some(fieldset => fieldset.disabled && !fieldset.querySelector(':scope > legend')?.contains(input)));
    sync();
    const observer = new MutationObserver(sync);
    fieldsets.forEach(fieldset => observer.observe(fieldset, {attributes:true, attributeFilter:['disabled']}));
    return () => observer.disconnect();
  }, []);
  React.useEffect(() => {
    const form = inputRef.current?.closest('form');
    if (!form || controlledValue !== undefined) return;
    const reset = () => {setUncontrolledValue(defaultValue); setQuery(''); setOpen(false); highlightedOption.current = null; onSearchChange?.('');};
    form.addEventListener('reset', reset);
    return () => form.removeEventListener('reset', reset);
  }, [controlledValue, defaultValue, onSearchChange]);
  const selectedCache = React.useRef(new Map<string | number, SearchableDropdownOption>());
  const values = Array.isArray(value) ? value : value == null ? [] : [value];
  const selected = values.filter(item => item !== '').map(item => options.find(option => option.value === item) ?? selectedCache.current.get(item) ?? {value: item, label: String(item)});
  const active = multiple ? selected : selected[0] ?? null;
  React.useEffect(() => {
    for (const option of selected) selectedCache.current.set(option.value, option);
    while (selectedCache.current.size > 1000) selectedCache.current.delete(selectedCache.current.keys().next().value!);
  });
  React.useEffect(() => {
    inputRef.current?.setCustomValidity(required && selected.length === 0 ? validationMessage : '');
  }, [required, validationMessage, selected.length]);
  const clear = () => {
    setQuery('');
    onSearchChange?.('');
    setOpen(false);
    highlightedOption.current = null;
    if (selected.length > 0 && !readOnly && !effectiveDisabled && clearable) changeValue(multiple ? [] : null);
  };

  const closeMenu = () => { setOpen(false); highlightedOption.current = null; if (!preserveSearchOnClose) { setQuery(''); onSearchChange?.(''); } };

  return <DropdownFooterContext.Provider value={dropdownFooter ? { content: dropdownFooter, id: footerId, input: () => inputRef.current, close: closeMenu, escape: () => { clear(); inputRef.current?.focus(); } } : null}><div dir={direction}>
    <Autocomplete<SearchableDropdownOption, boolean, boolean, false>
      slots={dropdownFooter ? { paper: DropdownPaper } : undefined}
      id={id}
      openText={controlText('common.open')}
      closeText={controlText('common.close')}
      clearText={controlText('common.clearSelection')}
      multiple={multiple}
      limitTags={limitTags}
      options={loading ? [] : options}
      value={active}
      open={open && !effectiveDisabled}
      onOpen={() => setOpen(true)}
      onClose={(event, reason) => {
        const related = 'relatedTarget' in event ? event.relatedTarget : null;
        if (dropdownFooter && reason === 'blur' && related instanceof Element && related.closest(`[data-aa-dropdown-footer="${footerId}"]`)) return;
        closeMenu();
      }}
      onHighlightChange={(_, option) => { highlightedOption.current = option; }}
      inputValue={open && !effectiveDisabled ? query : multiple ? '' : selected[0]?.label ?? (preserveSearchOnClose ? query : '')}
      onInputChange={(_, next, reason) => {
        if (reason !== 'input' && reason !== 'clear') return;
        if (reason === 'input' && freeText && multiple && tokenSeparators?.some(separator => separator && next.includes(separator))) {
          const separators = tokenSeparators.filter(Boolean).map(separator => separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
          const parts = next.split(new RegExp(separators.join('|')));
          const tail = parts.pop() ?? '';
          const committed = [...values];
          for (const part of parts.map(part => part.trim()).filter(Boolean)) {
            const option = options.find(option => String(option.value).toLocaleLowerCase() === part.toLocaleLowerCase() || option.label.toLocaleLowerCase() === part.toLocaleLowerCase());
            const item = option?.value ?? part;
            if (!option?.disabled && !committed.some(value => String(value).toLocaleLowerCase() === String(item).toLocaleLowerCase())) committed.push(item);
          }
          changeValue(committed); setQuery(tail); onSearchChange?.(tail); return;
        }
        setQuery(next); onSearchChange?.(next);
        if (freeText && !multiple && reason === 'input') changeValue(next || null);
      }}
      filterOptions={(items, state) => {
        const filtered = serverFiltered ? items : createFilterOptions<SearchableDropdownOption>({trim: true, stringify: option => `${option.label} ${option.searchText ?? ''}`})(items, state);
        const candidate = state.inputValue.trim();
        if (multiple && freeText && candidate && !items.some(option => String(option.value).toLocaleLowerCase() === candidate.toLocaleLowerCase() || option.label.toLocaleLowerCase() === candidate.toLocaleLowerCase())) {
          return [...filtered, {value:candidate, label:candidate, hint: language === 'ar' ? 'إضافة' : 'Add'}];
        }
        return filtered;
      }}
      onChange={(_, next, reason) => {
        if (reason === 'clear') { clear(); return; }
        changeValue(Array.isArray(next) ? next.map((option) => option.value) : next?.value ?? null);
        setQuery('');
      }}
      onKeyDown={(event) => {
        if (event.key === 'Tab' && !event.shiftKey && open && dropdownFooter) {
          const button = document.querySelector<HTMLButtonElement>(`[data-aa-dropdown-footer="${footerId}"] button:not(:disabled)`);
          if (button) { event.preventDefault(); button.focus(); }
        }
        if (event.key === 'Enter' && multiple && freeText && query.trim()) {
          const candidate = query.trim();
          const existing = options.find(option => String(option.value).toLocaleLowerCase() === candidate.toLocaleLowerCase() || option.label.toLocaleLowerCase() === candidate.toLocaleLowerCase());
          const highlightedExisting = highlightedOption.current && options.some(option => option.value === highlightedOption.current?.value);
          if (!existing && !highlightedExisting) {
            event.preventDefault();
            event.stopPropagation();
            (event as React.KeyboardEvent & { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
            if (!values.some(item => String(item).toLocaleLowerCase() === candidate.toLocaleLowerCase())) changeValue([...values, candidate]);
            setQuery('');
            onSearchChange?.('');
          }
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          clear();
        }
      }}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      getOptionDisabled={(option) => !!option.disabled}
      isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
      disabled={effectiveDisabled}
      readOnly={readOnly}
      loading={loading}
      disableClearable
      disableCloseOnSelect={multiple}
      noOptionsText={<><div style={{padding:'0 0 9px',fontSize:10,color:'var(--crm-muted, #817d75)'}}>{controlText('common.availableOptions')} · 0</div><div style={{padding:'12px 0',textAlign:'center'}}>{noResultsText ?? controlText('common.noMatchingResults')}</div></>}
      loadingText={loadingText ?? controlText('common.loading')}
      groupBy={() => ''}
      renderGroup={(group) => <li key={group.key} role="presentation"><div style={{padding:'9px 11px',fontSize:10,color:'var(--crm-muted, #817d75)'}}>{controlText('common.availableOptions')} · {React.Children.count(group.children)}</div><ul role="group" style={{padding:0,margin:0,listStyle:'none'}}>{group.children}</ul></li>}
      renderOption={(props, option, state) => {
        const { key, ...optionProps } = props;
        return <li key={key} {...optionProps}><span>{option.label}{option.hint && <small style={{display:'block',color:'var(--crm-muted, #817d75)'}}>{text(option.hint)}</small>}</span>{state.selected && <Check size={16} style={{ marginInlineStart: 'auto' }} />}</li>;
      }}
      renderInput={(params) => <TextField {...params} id={id} inputRef={assignInputRef} autoFocus={autoFocus} required={required && selected.length === 0} error={error}
        placeholder={selected.length ? selected.map((item) => item.label).join(', ') : options.find(option => option.value === '')?.label ?? displayPlaceholder} inputProps={{ ...params.inputProps, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, 'aria-haspopup': 'listbox', 'aria-expanded': open && !effectiveDisabled, 'data-aa-searchable': 'true', onBlur: (event: React.FocusEvent<HTMLInputElement>) => {params.inputProps.onBlur?.(event); onBlur?.(event);}, onFocus: (event: React.FocusEvent<HTMLInputElement>) => {params.inputProps.onFocus?.(event); onFocus?.(event);} }}
        InputProps={{ ...params.InputProps,
          startAdornment: <><InputAdornment position="start"><Search size={17} /></InputAdornment>{params.InputProps.startAdornment}</>,
          endAdornment: <span className="aa-search-actions">{clearable && !effectiveDisabled && !readOnly && (query || selected.length > 0) && <button type="button" aria-label={clearLabel ?? controlText('common.clearSelection')} onMouseDown={(event) => event.preventDefault()} onClick={clear}><X size={16} /></button>}<span aria-hidden="true" style={{height:19,borderInlineStart:'1px solid #e8e5df'}} />{params.InputProps.endAdornment}</span>,
        }} />}
      sx={{ '& .MuiOutlinedInput-root': { minHeight: '44px !important', padding: '0 8px !important', borderRadius: '9px', background: 'var(--crm-surface, #fff)', color:'var(--crm-text, #292620)', fontSize: 13, '&.Mui-focused': { boxShadow: '0 0 0 3px #e8e3d9' } }, '& .MuiAutocomplete-input': { minWidth: '0 !important', height: 'auto !important', padding: '0 !important', border: '0 !important', outline: '0 !important', boxShadow: 'none !important', background: 'transparent !important', font: 'inherit', color:'var(--crm-text, #292620) !important' }, '& .MuiChip-root': {backgroundColor:'var(--crm-soft, #eeebe5)',color:'var(--crm-text, #292620)'}, '& .MuiAutocomplete-input:focus-visible': { outline: 'none !important' }, '& .MuiInputAdornment-positionStart': { margin: '0 !important', marginInlineEnd: '8px !important' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d9d5cc' }, '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#716b60 !important', borderWidth: '1px !important' }, '& .aa-search-actions': { display: 'flex', alignItems: 'center', flexShrink: 0 }, '& .MuiAutocomplete-endAdornment': { position: 'static !important', top: 'auto !important', transform: 'none !important', display: 'inline-flex', alignItems: 'center' }, '& .aa-search-actions button': { minWidth: '0 !important', width: '28px !important', height: '28px !important', padding: '4px !important', margin: '0 !important', border: '0 !important', borderRadius: '5px !important', background: 'transparent !important', boxShadow: 'none !important', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#77736b' }, '& .aa-search-actions button:hover': { background: '#f0eee9 !important' } }}
      slotProps={{ listbox: { sx: { maxHeight: 246, color:'var(--crm-text, #292620)', backgroundColor:'var(--crm-surface, #fff)', '& .MuiAutocomplete-option': {color:'var(--crm-text, #292620) !important'}, '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: 'var(--crm-accent-soft, #eeebe5)', fontWeight: 650 }, '& .MuiAutocomplete-option.Mui-focused': { backgroundColor: 'var(--crm-soft, #f5f3ef)' } } }, paper: { sx: {'& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {color:'var(--crm-text, #292620)'}}, style: { borderRadius: 10, marginTop: 7, background:'var(--crm-surface, #fff)',color:'var(--crm-text, #292620)' } }, popper: { dir: direction ?? (language === 'ar' ? 'rtl' : undefined), style: { zIndex: 6000 } } }}
    />
    {name && (multiple ? values : [values[0] ?? '']).map((item) => <input key={item} type="hidden" name={name} value={item} disabled={effectiveDisabled} />)}
  </div></DropdownFooterContext.Provider>;
}
